/**
 * Pure page-level transforms for the contacts infinite-query cache. Kept free of
 * React Query runtime and the Supabase client so the count-adjustment logic is
 * unit-testable; the hooks wrap these in `setQueriesData`. Each returns a new
 * data object (or the input unchanged) and never mutates.
 */

import type { InfiniteData } from "@tanstack/react-query";
import type { Contact, ContactsPage } from "./types";

type ContactsData = InfiniteData<ContactsPage> | undefined;

/** Apply `fn` to the contact with `id` wherever it appears across the pages;
 *  pages without the id are returned untouched. Count is unchanged. */
export function mergeContactPages(
  data: ContactsData,
  id: string,
  fn: (c: Contact) => Contact,
): ContactsData {
  if (!data) return data;
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      contacts: page.contacts.map((c) => (c.id === id ? fn(c) : c)),
    })),
  };
}

/** Insert a new contact (prepended to page 1 - the page re-sorts the full set,
 *  so placement is cosmetic) and bump every page's roster count by one. */
export function insertContactPages(data: ContactsData, contact: Contact): ContactsData {
  if (!data) return data;
  return {
    ...data,
    pages: data.pages.map((page, i) => ({
      ...page,
      count: page.count + 1,
      contacts: i === 0 ? [contact, ...page.contacts] : page.contacts,
    })),
  };
}

/** Remove the contact by id and decrement the roster count - but only when the
 *  row was actually present in this query's cache, so a query that never held it
 *  keeps its count and rows intact. */
export function removeContactPages(data: ContactsData, id: string): ContactsData {
  if (!data) return data;
  const present = data.pages.some((p) => p.contacts.some((c) => c.id === id));
  if (!present) return data;
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      count: Math.max(0, page.count - 1),
      contacts: page.contacts.filter((c) => c.id !== id),
    })),
  };
}
